import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, Trophy, Target, IndianRupee, Percent, Calendar, Loader2, RefreshCw } from 'lucide-react';
import ImportDataModal from '../components/ImportDataModal';
import Navbar from '../components/Navbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { mockPortfolios, mockJournalData, mockUser } from '../mock';
import { getPortfolios, getPortfolio, getJournalData } from '../services/api';
import { analytics } from '../lib/analytics';
import type { Portfolio, JournalData } from '../types';
import { Broker, getBrokerColors, getTickerColor } from '../types';

const JournalPage = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  
  // State for API data
  const [portfolios, setPortfolios] = useState<Portfolio[]>(mockPortfolios);
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [journalData, setJournalData] = useState<JournalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user] = useState(mockUser);
  
  // Session storage key for this portfolio's journal filters
  const storageKey = `journal-filters-${portfolioId}`;
  
  // Load saved filters from sessionStorage
  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return null;
  };
  
  const savedFilters = getSavedFilters();
  
  // Filter state - initialize from sessionStorage if available
  const [searchQuery, setSearchQuery] = useState(savedFilters?.searchQuery || '');
  const [selectedBroker, setSelectedBroker] = useState(savedFilters?.selectedBroker || 'all');
  const [selectedCategory, setSelectedCategory] = useState(savedFilters?.selectedCategory || 'all');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(savedFilters?.dateFilter || 'all');
  const [customDateRange, setCustomDateRange] = useState(savedFilters?.customDateRange || { from: '', to: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    const filters = {
      searchQuery,
      selectedBroker,
      selectedCategory,
      dateFilter,
      customDateRange,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [searchQuery, selectedBroker, selectedCategory, dateFilter, customDateRange, storageKey]);

  // Fetch data from API
  const fetchData = async () => {
    if (!portfolioId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch portfolios list
      const portfoliosResponse = await getPortfolios();
      if (portfoliosResponse.success && portfoliosResponse.data) {
        setPortfolios(portfoliosResponse.data);
      }
      
      // Fetch current portfolio details
      const portfolioResponse = await getPortfolio(portfolioId);
      if (portfolioResponse.success && portfolioResponse.data) {
        setPortfolio(portfolioResponse.data);
      } else {
        // Fallback to mock if portfolio not found
        const mockPortfolio = mockPortfolios.find(p => p.id === portfolioId);
        if (mockPortfolio) {
          setPortfolio(mockPortfolio);
        }
      }
      
      // Fetch journal data
      const journalResponse = await getJournalData(portfolioId);
      if (journalResponse.success && journalResponse.data) {
        setJournalData(journalResponse.data);
      } else {
        // Fallback to mock data
        const mockData = mockJournalData[portfolioId];
        if (mockData) {
          setJournalData(mockData);
        } else {
          setError('No journal data found for this portfolio');
        }
      }
    } catch (err) {
      console.error('Error fetching journal data:', err);
      setError('Failed to load journal data');
      // Fallback to mock data on error
      const mockPortfolio = mockPortfolios.find(p => p.id === portfolioId);
      if (mockPortfolio) {
        setPortfolio(mockPortfolio);
      }
      const mockData = mockJournalData[portfolioId || ''];
      if (mockData) {
        setJournalData(mockData);
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [portfolioId]);

  const handleTabChange = (value: string) => {
    if (value === 'current') {
      navigate(`/portfolio/${portfolioId}/current`);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedBroker('all');
    setSelectedCategory('all');
    setDateFilter('all');
    setCustomDateRange({ from: '', to: '' });
    sessionStorage.removeItem(storageKey);
  };

  // Get date range based on filter
  const getDateRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case '1month':
        return { from: new Date(now.setMonth(now.getMonth() - 1)), to: new Date() };
      case '3months':
        return { from: new Date(now.setMonth(now.getMonth() - 3)), to: new Date() };
      case '1year':
        return { from: new Date(now.setFullYear(now.getFullYear() - 1)), to: new Date() };
      case 'custom':
        return {
          from: customDateRange.from ? new Date(customDateRange.from) : null,
          to: customDateRange.to ? new Date(customDateRange.to) : null
        };
      default:
        return null; // 'all' - no date filter
    }
  };

  const filteredTrades = journalData?.positions.filter(trade => {
    // Search filter
    const matchesSearch = trade.ticker.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Broker filter
    const matchesBroker = selectedBroker === 'all' || trade.broker === selectedBroker;
    
    // Category filter
    const matchesCategory = selectedCategory === 'all' || trade.category === selectedCategory;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all' && trade.closedDate) {
      const dateRange = getDateRange();
      if (dateRange && dateRange.from && dateRange.to) {
        const closedDate = new Date(trade.closedDate);
        matchesDate = closedDate >= dateRange.from && closedDate <= dateRange.to;
      }
    }
    
    return matchesSearch && matchesBroker && matchesCategory && matchesDate;
  }) || [];

  // Calculate filtered summary from filtered trades
  const filteredSummary = {
    totalPositions: journalData?.summary.totalPositions || 0,
    closedPositions: filteredTrades.length,
    winningPositions: filteredTrades.filter(t => t.outcome === 'win').length,
    losingPositions: filteredTrades.filter(t => t.outcome === 'loss').length,
    winRate: filteredTrades.length > 0 
      ? (filteredTrades.filter(t => t.outcome === 'win').length / filteredTrades.length) * 100 
      : 0,
    avgWinAmount: (() => {
      const wins = filteredTrades.filter(t => t.outcome === 'win');
      return wins.length > 0 ? wins.reduce((sum, t) => sum + t.realizedPnL, 0) / wins.length : 0;
    })(),
    avgLossAmount: (() => {
      const losses = filteredTrades.filter(t => t.outcome === 'loss');
      return losses.length > 0 ? Math.abs(losses.reduce((sum, t) => sum + t.realizedPnL, 0) / losses.length) : 0;
    })(),
    totalPnL: filteredTrades.reduce((sum, t) => sum + t.realizedPnL, 0),
    avgROI: filteredTrades.length > 0
      ? filteredTrades.reduce((sum, t) => sum + (t.roi || 0), 0) / filteredTrades.length
      : 0,
    avgHoldingPeriod: filteredTrades.length > 0
      ? filteredTrades.reduce((sum, t) => sum + (t.durationDays || 0), 0) / filteredTrades.length
      : 0,
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/journal`)}
          
          user={user}
        />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading journal...</span>
        </div>
      </div>
    );
  }

  if (!portfolio || !journalData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          portfolios={portfolios}
          selectedPortfolio={portfolioId}
          onPortfolioChange={(id) => navigate(`/portfolio/${id}/journal`)}
          
          user={user}
        />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-gray-500">{error || 'No journal data available'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        portfolios={portfolios}
        selectedPortfolio={portfolioId}
        onPortfolioChange={(id) => navigate(`/portfolio/${id}/journal`)}
        
        user={user}
      />

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Header with Centered Tabs */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{portfolio.name}</h1>
            <p className="text-sm text-gray-500">Last synced: Today at 2:45 PM</p>
          </div>
          
          <Tabs defaultValue="journal" className="w-auto" onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="current">Current</TabsTrigger>
              <TabsTrigger value="journal">Journal</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Button
            variant="outline"
            className="gap-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            onClick={() => setIsImportModalOpen(true)}
          >
            <RefreshCw className="w-4 h-4" />
            Import Data
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4 items-center">
          {dateFilter === 'custom' ? (
            <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[280px] justify-start text-left font-normal border-emerald-300 hover:border-emerald-400">
                  <Calendar className="mr-2 h-4 w-4 text-emerald-600" />
                  {customDateRange.from && customDateRange.to
                    ? `${new Date(customDateRange.from).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} - ${new Date(customDateRange.to).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                    : <span className="text-gray-500">Select custom date range</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[340px] p-0" align="start">
                <div className="bg-gradient-to-br from-emerald-50 to-blue-50 p-4 border-b">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    Select Date Range
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">Choose a custom period for your journal</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                      From Date
                    </label>
                    <Input
                      type="date"
                      value={customDateRange.from}
                      onChange={(e) => setCustomDateRange({ ...customDateRange, from: e.target.value })}
                      className="border-gray-300 focus:border-emerald-400 focus:ring-emerald-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                      To Date
                    </label>
                    <Input
                      type="date"
                      value={customDateRange.to}
                      onChange={(e) => setCustomDateRange({ ...customDateRange, to: e.target.value })}
                      className="border-gray-300 focus:border-blue-400 focus:ring-blue-400"
                    />
                  </div>
                  {customDateRange.from && customDateRange.to && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-800">
                        <span className="font-medium">Selected Range:</span> {Math.ceil((new Date(customDateRange.to).getTime() - new Date(customDateRange.from).getTime()) / (1000 * 60 * 60 * 24))} days
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setShowCustomDatePicker(false);
                        console.log('Apply date range:', customDateRange);
                      }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      disabled={!customDateRange.from || !customDateRange.to}
                    >
                      Apply Filter
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDateFilter('1month');
                        setCustomDateRange({ from: '', to: '' });
                        setShowCustomDatePicker(false);
                      }}
                      className="flex-1"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          ) : (
            <Select value={dateFilter} onValueChange={(value) => {
              setDateFilter(value);
              if (value === 'custom') {
                setShowCustomDatePicker(true);
              }
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="1month">Last 1 Month</SelectItem>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="1year">Last Year</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          )}

          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search stocks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {portfolio.categories.map(cat => (
                <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedBroker} onValueChange={setSelectedBroker}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Brokers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brokers</SelectItem>
              {portfolio?.brokers.map((broker) => (
                <SelectItem key={broker} value={broker}>{broker}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={handleResetFilters}>Reset Filters</Button>
        </div>

        {/* Summary Analytics - Uses filtered data */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">{filteredSummary.totalPositions}</p>
            <p className="text-xs text-blue-700">Total Positions</p>
            <p className="text-xs text-blue-600 mt-1">All time</p>
          </Card>

          <Card className="p-4 bg-purple-50 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">{filteredSummary.closedPositions}</p>
            <p className="text-xs text-purple-700">Closed Positions</p>
            <p className="text-xs text-purple-600 mt-1">{filteredSummary.totalPositions > 0 ? ((filteredSummary.closedPositions / filteredSummary.totalPositions) * 100).toFixed(0) : 0}% of total</p>
          </Card>

          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <Trophy className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-900">{filteredSummary.winningPositions}</p>
            <p className="text-xs text-green-700">Winning Positions</p>
            <p className="text-xs text-green-600 mt-1">{filteredSummary.winRate.toFixed(0)}% win rate</p>
          </Card>

          <Card className="p-4 bg-red-50 border-red-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-900">{filteredSummary.losingPositions}</p>
            <p className="text-xs text-red-700">Losing Positions</p>
            <p className="text-xs text-red-600 mt-1">{filteredSummary.closedPositions > 0 ? (100 - filteredSummary.winRate).toFixed(0) : 0}% loss rate</p>
          </Card>

          <Card className="p-4 bg-violet-50 border-violet-200">
            <div className="flex items-center justify-between mb-2">
              <IndianRupee className="w-5 h-5 text-violet-600" />
            </div>
            <p className="text-2xl font-bold text-violet-900">₹{filteredSummary.avgWinAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-violet-700">Avg Win Amount</p>
            <p className="text-xs text-violet-600 mt-1">Per position</p>
          </Card>

          <Card className="p-4 bg-orange-50 border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingDown className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-orange-900">₹{filteredSummary.avgLossAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-orange-700">Avg Loss Amount</p>
            <p className="text-xs text-orange-600 mt-1">Per position</p>
          </Card>

          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <p className={`text-2xl font-bold ${filteredSummary.totalPnL >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
              {filteredSummary.totalPnL >= 0 ? '+' : ''}₹{filteredSummary.totalPnL.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-emerald-700">Total P&L</p>
            <p className="text-xs text-emerald-600 mt-1">Filtered results</p>
          </Card>

          <Card className="p-4 bg-cyan-50 border-cyan-200">
            <div className="flex items-center justify-between mb-2">
              <Percent className="w-5 h-5 text-cyan-600" />
            </div>
            <p className={`text-2xl font-bold ${filteredSummary.avgROI >= 0 ? 'text-cyan-900' : 'text-red-900'}`}>
              {filteredSummary.avgROI.toFixed(1)}%
            </p>
            <p className="text-xs text-cyan-700">Avg ROI</p>
            <p className="text-xs text-cyan-600 mt-1">Per position</p>
          </Card>
        </div>

        {/* Historical Trades */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Historical Trades</h2>
            <span className="text-sm text-gray-500">Sorted by: Closed Date (Newest First)</span>
          </div>

          <div className="space-y-3">
            {filteredTrades?.map((trade) => {
              const isProfitable = trade.outcome === 'win';
              const categoryColor = portfolio.categories.find(c => c.name === trade.category)?.color || '#6B7280';
              return (
                <Card
                  key={trade.id}
                  className={`p-4 hover:shadow-md transition-shadow cursor-pointer border-l-4 ${isProfitable ? 'border-l-green-500' : trade.outcome === 'loss' ? 'border-l-red-500' : 'border-l-gray-400'}`}
                  onClick={() => {
                    analytics.trackPositionDetailClicked(trade.id, trade.ticker, 'journal');
                    navigate(`/portfolio/${portfolioId}/stock/${trade.id}`);
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    {/* Left - Stock Info */}
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm"
                        style={{ backgroundColor: getTickerColor(trade.ticker) }}
                      >
                        {trade.ticker.substring(0, 3)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{trade.ticker}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs px-2 py-0.5 font-medium rounded"
                            style={{ backgroundColor: `${categoryColor}20`, color: categoryColor }}
                          >
                            {trade.category}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getBrokerColors(trade.broker).bg} ${getBrokerColors(trade.broker).text}`}>
                            {trade.broker}
                          </span>
                          <span className="text-xs text-gray-500">{trade.trancheCount} tranches</span>
                          <span className="text-xs text-gray-500">Closed: {trade.closedDate}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right - Performance */}
                    <div className="flex items-center gap-8">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Entry → Exit</p>
                        <p className="text-sm font-medium text-gray-900">
                          ₹{trade.avgBuyPrice} → ₹{trade.exitPrice}
                        </p>
                      </div>

                      <div className="text-center min-w-[100px]">
                        <p className="text-xs text-gray-500 mb-1">P&L</p>
                        <p className={`text-lg font-bold ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                          {isProfitable ? '+' : ''}₹{trade.realizedPnL.toLocaleString('en-IN')}
                        </p>
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">ROI</p>
                        <p className={`text-lg font-bold ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                          {isProfitable ? '+' : ''}{trade.roi}%
                        </p>
                      </div>

                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Duration</p>
                        <p className="text-sm font-medium text-gray-900">{trade.durationDays} days</p>
                      </div>
                    </div>
                  </div>

                  {/* Bottom row - Tranche breakdown and SL */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-xs">
                      {trade.trancheBreakdown && trade.trancheBreakdown.length > 0 ? (
                        trade.trancheBreakdown.map((tranche, idx) => (
                          <span 
                            key={tranche.id} 
                            className={`px-1.5 py-0.5 rounded ${
                              tranche.type === 'buy' 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-red-50 text-red-700'
                            }`}
                          >
                            T{idx + 1} {tranche.type === 'buy' ? '+' : '-'}{Math.abs(tranche.percentage)}%
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-500">{trade.trancheCount} tranche{trade.trancheCount !== 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">SL:</span> ₹{trade.stoploss.toLocaleString('en-IN')}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Import Data Modal */}
      <ImportDataModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        portfolioId={portfolioId!}
        portfolioBrokers={portfolio?.brokers || []}
        onImportComplete={() => {
          // Refresh data after import
          fetchData();
        }}
      />
    </div>
  );
};

export default JournalPage;
